import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, useFetch } from "../../components/crm/ui";
import { Plus, Trash2, Mail, Phone, Wrench, Info } from "lucide-react";

const EMPTY = { name: "", phone: "", email: "", skill: "" };

export default function ServiceTeamTechnicians() {
  const { data: techs, reload } = useFetch("/technicians");
  const [me, setMe] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/service-team/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const isFranchisee = me?.type === "service_franchisee";
  const isManager = me?.type === "service_manager";

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      await api.post("/technicians", form);
      toast.success(`${form.name} added to your roster`);
      setModalOpen(false); setForm(EMPTY); reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  }

  async function remove(t) {
    if (!confirm(`Remove ${t.name} from your roster?`)) return;
    try { await api.delete(`/technicians/${t.id}`); toast.success("Removed"); reload(); }
    catch (err) { toast.error(err.message); }
  }

  return (
    <div className="space-y-6" data-testid="service-technicians-page">
      <PageHeader
        overline={isManager ? "Read-only" : "My Roster"}
        title={isManager ? "My Technicians" : "My Technicians"}
        action={
          isFranchisee ? (
            <PrimaryButton onClick={() => setModalOpen(true)} testid="service-tech-add">
              <Plus className="w-4 h-4" /> Add Technician
            </PrimaryButton>
          ) : null
        }
      />

      {isManager && (
        <div
          className="bg-[#FFF7E6] border border-[#F5DAB8] p-3 text-xs text-[#7A4200] flex items-start gap-2"
          data-testid="service-tech-manager-notice"
        >
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            As a Service Manager you cannot add technicians directly. Ask the Admin to create the technician
            in <code>/crm/technicians</code> and tag the "Service Team" field to you.
          </div>
        </div>
      )}

      <div className="bg-white border border-[#DDE5D8]">
        <table className="w-full text-sm">
          <thead className="bg-[#0E2818] text-[#D4FF7A]">
            <tr>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Name</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Contact</th>
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Skill</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {techs.map((t) => (
              <tr key={t.id} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1]" data-testid={`service-tech-row-${t.id}`}>
                <td className="px-4 py-3 font-display font-semibold text-[#0F1A12]">{t.name}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-[#475569]">
                  <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{t.email}</div>
                  <div className="flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{t.phone}</div>
                </td>
                <td className="px-4 py-3 text-xs text-[#0F1A12]">{t.skill || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {isFranchisee ? (
                    <button onClick={() => remove(t)} data-testid={`service-tech-delete-${t.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-[#FCD7D7] text-[#B91C1C] hover:bg-[#FCD7D7]">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  ) : (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">read-only</span>
                  )}
                </td>
              </tr>
            ))}
            {techs.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-sm font-mono text-[#94A3B8]">
                {isManager ? "No technicians tagged to you yet — ask the Admin." : "No technicians yet. Click \"Add Technician\" to start."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add Technician">
        <div className="space-y-3">
          <Field label="Name *"><TextInput value={form.name} onChange={(e) => up("name", e.target.value)} data-testid="tech-name" /></Field>
          <Field label="Phone *"><TextInput value={form.phone} onChange={(e) => up("phone", e.target.value)} data-testid="tech-phone" /></Field>
          <Field label="Email *"><TextInput type="email" value={form.email} onChange={(e) => up("email", e.target.value)} data-testid="tech-email" /></Field>
          <Field label="Skill"><TextInput value={form.skill} onChange={(e) => up("skill", e.target.value)} placeholder="e.g. Solar installer, AMC technician" data-testid="tech-skill" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setModalOpen(false)} className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C]">Cancel</button>
            <PrimaryButton onClick={save} disabled={saving || !form.name || !form.phone || !form.email} testid="tech-save">
              {saving ? "Adding…" : "Add to roster"}
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}
